import type { RuleTargetDto } from '@citilight-monitor/contracts';
import { Gauge, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCreateRuleTarget, useDeleteRuleTarget, useUpdateRuleTarget } from '../../lib/api';
import { usePagination } from '../../lib/pagination';
import { useToast } from '../../lib/toast';
import { Button, EmptyState, Table } from '../atoms';
import { Pagination } from '../molecules';
import { RuleTargetFormDialog } from './RuleTargetFormDialog';
import { TargetMetricsDialog } from './TargetMetricsDialog';

const PAGE_SIZE = 10;

export function RuleTargetsTable({ targets }: { targets: RuleTargetDto[] }) {
  const [dialogTarget, setDialogTarget] = useState<RuleTargetDto | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [metricsTarget, setMetricsTarget] = useState<RuleTargetDto | undefined>(undefined);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const { showSuccess } = useToast();
  const createRuleTarget = useCreateRuleTarget();
  const updateRuleTarget = useUpdateRuleTarget();
  const deleteRuleTarget = useDeleteRuleTarget();
  const { page, totalPages, pageItems, start, end, total, setPage } = usePagination(targets, PAGE_SIZE);

  function openCreate() {
    setDialogTarget(undefined);
    setDialogOpen(true);
  }

  function openEdit(target: RuleTargetDto) {
    setDialogTarget(target);
    setDialogOpen(true);
  }

  function openMetrics(target: RuleTargetDto) {
    setMetricsTarget(target);
    setMetricsOpen(true);
  }

  async function remove(target: RuleTargetDto) {
    if (!window.confirm(`Delete "${target.name}"? Rules on this target will stop evaluating.`)) return;
    await deleteRuleTarget.mutateAsync(target.id);
    showSuccess('Target deleted.');
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end px-5 pt-4">
        <Button variant="primary" icon={<Plus size={14} aria-hidden />} onClick={openCreate}>
          Add target
        </Button>
      </div>

      <Table>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Kind</th>
            <th scope="col">Host</th>
            <th scope="col">Metrics endpoint</th>
            <th scope="col" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {pageItems.map((target) => (
            <tr key={target.id}>
              <td className="font-medium text-fg">{target.name}</td>
              <td>{target.kind === 'SERVER' ? 'Server' : 'Application'}</td>
              <td className="font-mono text-xs">{target.host}</td>
              <td className="font-mono text-xs">
                {target.metricsEndpoint.protocol}://{target.host}:{target.port}{target.metricsEndpoint.path}
              </td>
              <td>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" aria-label="Metrics" onClick={() => openMetrics(target)} icon={<Gauge size={14} aria-hidden />} />
                  <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => openEdit(target)} icon={<Pencil size={14} aria-hidden />} />
                  <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => void remove(target)} icon={<Trash2 size={14} aria-hidden />} />
                </div>
              </td>
            </tr>
          ))}
          {targets.length === 0 && (
            <tr>
              <td colSpan={5}>
                <EmptyState>No targets yet. Add a server or application to monitor.</EmptyState>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination page={page} totalPages={totalPages} start={start} end={end} total={total} onPageChange={setPage} />

      <RuleTargetFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        target={dialogTarget}
        onSubmit={async (dto) => {
          if (dialogTarget) {
            await updateRuleTarget.mutateAsync({ id: dialogTarget.id, body: dto });
            showSuccess('Target updated.');
          } else {
            await createRuleTarget.mutateAsync(dto);
            showSuccess('Target added.');
          }
        }}
      />

      <TargetMetricsDialog open={metricsOpen} onClose={() => setMetricsOpen(false)} target={metricsTarget} />
    </div>
  );
}
